import useContextActions from "src/components/providers/ActionsQueueContextProvider/useContextActions";
import { FileMetadata } from "src/requests/files/types";
import { RawFile, FileConfigParams } from "../types";
import { getDeleteActionIdForRow, getUploadActionIdForRow } from "../utils";
import useDeleteFiles from "./useDeleteFiles";
import useUploadNewFiles from "./useUploadNewFiles";

type AddUploadToActionsQueue = (filesToUpload: RawFile[]) => void;
type AddDeleteToActionsQueue = (filesToDelete: FileMetadata[]) => void;
type RemoveActionsFromQueue = () => void;

interface ReturnType {
  addUploadToActionsQueue: AddUploadToActionsQueue;
  addDeleteToActionsQueue: AddDeleteToActionsQueue;
  removeActionsForRowFromQueue: RemoveActionsFromQueue;
}

const useAddFileActionsToQueue = (
  uniqueIdentifier: string,
  params: FileConfigParams
): ReturnType => {
  const { updateActionsQueue, removeActionsFromQueue } = useContextActions();
  const uploadNewFiles = useUploadNewFiles(params);
  const deleteFiles = useDeleteFiles(params);

  const { templateId, row } = params;

  const addUploadToActionsQueue: AddUploadToActionsQueue = (filesToUpload) => {
    const actionKey = getUploadActionIdForRow(
      templateId,
      row?.keyValue ?? 0,
      uniqueIdentifier
    );

    const actionValue = async (): Promise<void> =>
      uploadNewFiles(filesToUpload);

    updateActionsQueue(actionKey, actionValue);
  };

  const addDeleteToActionsQueue: AddDeleteToActionsQueue = (filesToDelete) => {
    const actionKey = getDeleteActionIdForRow(
      templateId,
      row?.keyValue ?? 0,
      uniqueIdentifier
    );

    const actionValue = async (): Promise<void> => deleteFiles(filesToDelete);

    updateActionsQueue(actionKey, actionValue);
  };

  const removeActionsForRowFromQueue: RemoveActionsFromQueue = () => {
    const deleteKey = getDeleteActionIdForRow(
      templateId,
      row?.keyValue ?? 0,
      uniqueIdentifier
    );

    const uploadKey = getUploadActionIdForRow(
      templateId,
      row?.keyValue ?? 0,
      uniqueIdentifier
    );

    removeActionsFromQueue([deleteKey, uploadKey]);
  };

  return {
    addUploadToActionsQueue,
    addDeleteToActionsQueue,
    removeActionsForRowFromQueue,
  };
};

export default useAddFileActionsToQueue;
